import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { iapAvailable, ensureIap, findPackage, purchasePackage, restorePurchases, openManageSubscription } from "@/lib/iap";

// In-app (App Store / Google Play) subscribe button for one tier, used on Pricing inside the native app.
export function NativeIapButton({ tier, billing, isCurrent, source, variant, className, onDone, testId }) {
  const { user } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !iapAvailable()) return;
    let off = false;
    ensureIap(user.id).then(() => findPackage(tier, billing)).then((p) => { if (!off) setPkg(p); }).catch(() => {});
    return () => { off = true; };
  }, [user, tier, billing]);

  if (isCurrent) {
    return source === "revenuecat" ? (
      <Button variant="outline" className={className} onClick={() => openManageSubscription()} data-testid={`${testId}-manage`}>
        Manage subscription
      </Button>
    ) : (
      <p className="mt-4 text-xs text-muted-foreground text-center" data-testid={`${testId}-native-note`}>
        You're subscribed. This plan is managed from your web account.
      </p>
    );
  }
  if (!iapAvailable()) {
    return <p className="mt-4 text-xs text-muted-foreground text-center" data-testid={`${testId}-native-note`}>In-app purchases are coming soon.</p>;
  }
  if (!user) {
    return <Button asChild variant={variant} className={className} data-testid={testId}><Link to="/auth">Sign in to subscribe</Link></Button>;
  }

  const buy = async () => {
    if (!pkg) return;
    setBusy(true);
    try {
      if (await purchasePackage(pkg)) {
        toast.success("Subscription active — thank you!");
        await onDone?.();
      }
    } catch (e) {
      toast.error(e?.message || "Purchase didn't complete");
    } finally {
      setBusy(false);
    }
  };

  const period = billing === "yearly" ? "yr" : "mo";
  return (
    <Button variant={variant} className={className} disabled={busy || !pkg} onClick={buy} data-testid={testId}>
      {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>
        : pkg ? `Subscribe · ${pkg.product.priceString}/${period}` : <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading price…</>}
    </Button>
  );
}

// Restore button + the auto-renew disclosure Apple/Google require on a paywall.
export function NativeIapFooter({ onDone }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!iapAvailable()) return null;
  const restore = async () => {
    setBusy(true);
    try {
      await ensureIap(user?.id);
      const plan = await restorePurchases();
      toast.success(plan === "free" ? "No active purchases found" : "Purchases restored");
      await onDone?.();
    } catch (e) {
      toast.error(e?.message || "Restore failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 max-w-3xl mx-auto text-center space-y-2" data-testid="native-iap-footer">
      {user && (
        <Button variant="ghost" size="sm" onClick={restore} disabled={busy} data-testid="native-iap-restore">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}Restore purchases
        </Button>
      )}
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Subscriptions renew automatically at the price shown unless cancelled at least 24 hours before the end of the
        current period. Payment is charged to your store account; manage or cancel anytime in your store settings.{" "}
        <Link to="/trust" className="underline">Terms of Use</Link> · <Link to="/trust" className="underline">Privacy Policy</Link>
      </p>
    </div>
  );
}
